import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { Hourglass, Users, Crown, ShieldAlert, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LeagueIcon, LeagueIconField } from "@/components/LeagueIcon";
import { api, errMsg } from "@/lib/api";
import { DemoBadge, ErrorBox, Spinner, AVATAR_ICONS } from "@/components/Bits";
import { StandingsTab, MemberBreakdown } from "@/pages/league/StandingsTab";
import MembersTab from "@/pages/league/MembersTab";
import InvitationsTab from "@/pages/league/InvitationsTab";
import SelectionTab from "@/pages/league/SelectionTab";
import { RulesTab, JournalTab } from "@/pages/league/InfoTabs";
import SettingsTab from "@/pages/league/SettingsTab";
import RulesEditor from "@/pages/league/RulesEditor";
import OverviewTab from "@/pages/league/OverviewTab";
import ChallengesTab from "@/pages/league/ChallengesTab";
import ActivityTab from "@/pages/league/ActivityTab";
import PalmaresTab from "@/pages/league/PalmaresTab";
import LifecycleSection from "@/pages/league/LifecycleSection";
import { BrandHero } from "@/components/BrandBackdrop";
import { brandVars } from "@/lib/brand";

function RulesBanner({ league, reload }) {
  if (league.rules_accepted !== false || !league.rules_have_penalties) return null;
  const accept = async () => {
    try { await api.post(`/leagues/${league.id}/rules/accept`); toast.success("Règles acceptées"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="rules-accept-banner" className="mb-5 flex flex-col gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="flex items-start gap-2 text-sm text-amber-100"><ShieldAlert size={16} className="mt-0.5 shrink-0" /> Ce barème comporte des points négatifs. Lisez les règles et acceptez-les pour participer en connaissance de cause.</p>
      <div className="flex gap-2">
        <Link to={`/ligues/${league.id}/regles`} className="mpl-btn-ghost">Lire</Link>
        <button onClick={accept} className="mpl-btn-primary" data-testid="rules-accept-button">J'accepte</button>
      </div>
    </div>
  );
}

export default function LeagueDetail() {
  const { id, tab = "apercu" } = useParams();
  const { user, setCurrentLeagueId } = useAuth();
  const [league, setLeague] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api.get(`/leagues/${id}`).then(({ data }) => setLeague(data)).catch((e) => setError(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (league && !league.observer) setCurrentLeagueId(league.id); }, [league]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorBox message={error} />;
  if (!league) return <Spinner />;
  const obs = Boolean(league.observer);
  const owner = league.owner_id === user.id;
  const admin = user.role === "superadmin";
  const ALL = { rules: true, selection: true, featured: true, social: true, info: true, transfer: true };
  const perms = admin || (owner && !league.brand) ? ALL : owner ? (league.owner_perms || {}) : {};
  const mgr = Object.values(perms).some(Boolean);
  const locked = owner && league.brand && !admin;
  const Emblem = AVATAR_ICONS[league.emblem];
  const tabs = [["apercu", "Vue d'ensemble"], ["classement", "Classement"], ["defis", "Défis"], ["activite", "Activité"], ["membres", "Membres"],
    ...(owner ? [["invitations", "Invitations"]] : []), ["palmares", "Palmarès"], ...(obs && !mgr ? [] : [["parametres", "Paramètres"]])];
  const tabProps = { league: obs ? { ...league, archived: true } : league, owner, reload: load };
  const mgrProps = { league, owner: mgr, reload: load, perms, admin };
  const active = tab.startsWith("membre-") ? "classement" : ["regles", "journal", "selection"].includes(tab) ? "parametres" : tab;

  return (
    <div className={league.brand ? "mpl-branded" : undefined} style={brandVars(league.brand)} data-testid="league-page">
      <header className="mb-5 mpl-rise">
        <Link to="/ligues" className="mpl-eyebrow hover:text-white" data-testid="league-back-link">← Mes ligues</Link>
        {league.brand ? <div className="mt-2"><BrandHero brand={league.brand} name={league.name} /></div> : (
        <div className="mt-2 flex items-center gap-3">
          {league.icon_url ? <LeagueIcon l={league} className="h-12 w-12 rounded-2xl" testId="league-header-icon" /> : Emblem && <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#B7F52A] text-[#05070D]"><Emblem size={24} /></span>}
          <h1 data-testid="league-name" className="min-w-0 font-display text-4xl font-extrabold uppercase leading-none [overflow-wrap:anywhere]">{league.name}</h1>
        </div>)}
        {league.description && <p className="mt-2 text-sm text-slate-400">{league.description}</p>}
        {league.league_type === "EXPERIENCE" && league.experience_slug && (
          <nav className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#B7F52A]/30 bg-[#B7F52A]/[0.06] p-3 text-xs" aria-label="Experience" data-testid="league-experience-nav">
            <span className="font-bold text-[#B7F52A]">Ma Petite Ligue Experiences</span>
            {[["", "Accueil Experience"], ["#lot", "Le lot"], ["#gagner", "Comment gagner"], ["#reglement", "Règlement"], ["#partenaires", "Partenaires"]].map(([h, l]) => (
              <Link key={l} to={`/experience/${league.experience_slug}${h}`} className="rounded-full border border-white/10 px-2.5 py-1 font-semibold text-slate-200 hover:bg-white/5" data-testid={`league-exp-link-${h.slice(1) || "home"}`}>{l}</Link>))}
          </nav>)}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {league.is_demo && <DemoBadge />}
          <span className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-slate-300" data-testid="league-member-count"><Users size={12} /> {league.member_count}{league.max_members ? `/${league.max_members}` : ""}</span>
          <span className="rounded-full border border-white/10 px-2.5 py-1 text-slate-300">{league.mode === "custom" ? "Personnalisée" : "Classique"} · {league.scoring_rule.label}</span>
          <span data-testid="league-status" className={`flex items-center gap-1 rounded-full border px-2.5 py-1 font-semibold ${league.competition_started ? "border-[#B7F52A]/30 text-[#B7F52A]" : "border-amber-400/30 text-amber-300"}`}>
            {!league.competition_started && <Hourglass size={11} />} {league.status_label}
          </span>
          {owner && <span className="flex items-center gap-1 text-[#B7F52A]"><Crown size={12} /> Propriétaire</span>}
        </div>
        {obs && <p data-testid="league-observer-banner" className="mt-3 flex items-center gap-2 rounded-xl border border-sky-400/25 bg-sky-400/[0.06] p-3 text-xs text-sky-100"><Eye size={14} className="shrink-0" /> Mode observateur : tu vois la ligue comme un joueur, sans y apparaître ni pouvoir pronostiquer, écrire ou défier.</p>}
        {league.archived && <p data-testid="league-archived-banner" className="mt-3 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-xs text-slate-300">Ligue archivée : l'historique reste consultable, mais plus aucun pronostic, défi ou message n'est accepté.</p>}
      </header>
      {!obs && <RulesBanner league={league} reload={load} />}
      <nav className="mpl-scroll-x -mx-4 mb-6 flex gap-1 overflow-x-auto border-b border-white/[0.06] px-4">
        {tabs.map(([k, l]) => (
          <NavLink key={k} to={`/ligues/${id}/${k}`} data-testid={`league-tab-${k}`}
            className={`shrink-0 border-b-2 px-3 pb-3 text-sm font-semibold transition-colors ${active === k ? "border-[#B7F52A] text-white" : "border-transparent text-slate-400 hover:text-white"}`}
            style={active === k && league.brand ? { borderColor: "var(--brand-accent)" } : undefined}>{l}</NavLink>
        ))}
      </nav>
      {tab === "apercu" && <OverviewTab {...tabProps} />}
      {tab === "classement" && <StandingsTab {...tabProps} />}
      {tab.startsWith("membre-") && <MemberBreakdown {...tabProps} memberId={tab.slice(7)} />}
      {tab === "defis" && <ChallengesTab {...tabProps} />}
      {tab === "activite" && <ActivityTab {...tabProps} />}
      {tab === "membres" && <MembersTab {...tabProps} />}
      {tab === "invitations" && owner && <InvitationsTab {...tabProps} />}
      {tab === "palmares" && <PalmaresTab {...tabProps} />}
      {tab === "selection" && (!obs || mgr) && perms.selection && <SelectionTab {...mgrProps} />}
      {tab === "regles" && <RulesTab {...tabProps} />}
      {tab === "journal" && <JournalTab {...tabProps} />}
      {tab === "parametres" && (!obs || mgr) && (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2 text-xs">
            <Link to={`/ligues/${id}/regles`} className="mpl-btn-ghost" data-testid="settings-link-rules">Règles et barème</Link>
            {league.mode === "custom" && perms.selection && <Link to={`/ligues/${id}/selection`} className="mpl-btn-ghost" data-testid="settings-link-selection">Sélection des matchs</Link>}
            <Link to={`/ligues/${id}/journal`} className="mpl-btn-ghost" data-testid="settings-link-journal">Historique des modifications</Link>
          </div>
          {user.role === "superadmin" && !owner && <p className="rounded-xl border border-[#B7F52A]/30 bg-[#B7F52A]/[0.06] p-3 text-xs text-[#E8FFC0]" data-testid="settings-admin-banner">Mode administrateur : tu modifies les paramètres de cette ligue sans y jouer. Chaque changement est noté dans l'historique.</p>}
          {locked && !Object.values(perms).every(Boolean) && <p className="mpl-card p-4 text-sm text-slate-300" data-testid="settings-corp-locked">Ligue d'entreprise : {mgr ? "certains paramètres" : "les règles, le barème, la période, le match vedette et la propriété"} sont gérés par l'administration de Ma Petite Ligue. Écris à hello@mapetiteligue.com pour toute modification.</p>}
          {(admin || (!league.brand && perms.info)) && !league.archived && <LeagueIconField league={league} reload={load} />}
          {(perms.rules || perms.info) && !league.archived && <RulesEditor {...mgrProps} />}
          {mgr && !league.archived && <SettingsTab {...mgrProps} />}
          {owner && !league.lifecycle_locked && <LifecycleSection league={league} reload={load} />}
        </div>
      )}
    </div>
  );
}
